import { useState } from 'react';
import { Alert, Anchor, Badge, Button, Group, Image, Modal, Paper, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import {
  ASSET_SET_STATE_COLORS,
  ASSET_SET_STATE_LABELS,
  BRAND_KIT_COPY as C,
  BRAND_KIT_REASON_MAX,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import type { FactoryAssetSet } from '@/lib/types/AppFactory';
import { AppFactoryUtils } from '../AppFactoryUtils';
import { BrandKitUtils } from '../BrandKitUtils';
import { BrandKitQualityTable } from './BrandKitQualityTable';
import { RokuScreenPreviews } from './RokuScreenPreviews';
import { TvDevicePreviews } from './TvDevicePreviews';

interface AssetSetDraftCardProps {
  /** The pending draft. */
  set: FactoryAssetSet;
  /** Whose image set it is. */
  scope: FactoryAssetPlatform;
  /** The TV platforms that are on for the app: names, screens and quality rows follow them. */
  tv: readonly TvPlatformKey[];
  /** That platform's sets (a draft made before the live images says so). */
  assetSets: FactoryAssetSet[];
  disabled?: boolean;
  /** A decision is in flight. */
  busy?: boolean;
  /** Operators may approve a blocked draft with a reason. */
  isOperator?: boolean;
  onApprove: (set: FactoryAssetSet) => void;
  onDiscard: (set: FactoryAssetSet) => void;
  onOverride: (set: FactoryAssetSet, reason: string) => void;
}

/**
 * One pending draft, reviewed: the TV screens that show it at their true
 * size, the quality gate per screen, what blocks it with the fix, and the
 * decision. Shared by the Brand Kit tab (drafts rendered from the kit) and a
 * platform's Images tab (an upload, or a draft from before the kit). A draft
 * the gate blocks cannot be approved — except by an operator, with a reason.
 */
export function AssetSetDraftCard({
  set,
  scope,
  tv,
  assetSets,
  disabled,
  busy,
  isOperator,
  onApprove,
  onDiscard,
  onOverride,
}: AssetSetDraftCardProps) {
  const [overriding, setOverriding] = useState(false);
  const [reason, setReason] = useState('');
  const passed = BrandKitUtils.gatePassed(set);
  const fixes = BrandKitUtils.blockedFixes(set, tv);
  const advice = BrandKitUtils.adviceMessages(set);
  const newerLive = BrandKitUtils.draftPredatesLive(set, assetSets);
  const files = BrandKitUtils.previewFileMap(set, []);
  const manual = set.source === 'manual';

  const submitOverride = () => {
    const text = reason.trim();
    if (!text) return;
    // The reason stays in the box: a refused override must not make the operator type it again
    // (an approved one unmounts this card).
    setOverriding(false);
    onOverride(set, text);
  };

  return (
    <Paper withBorder radius="md" p="md" data-testid="asset-set-draft">
      <Stack gap="md">
        <Group gap="xs">
          <Text fw={600} size="sm">
            {BrandKitUtils.scopeLabel(scope, tv)}
          </Text>
          <Badge variant="light" color={passed ? ASSET_SET_STATE_COLORS.generated : 'red'} size="sm">
            {passed ? ASSET_SET_STATE_LABELS.generated : 'Needs a fix'}
          </Badge>
          {manual && (
            <Badge variant="outline" color="gray" size="sm">
              Uploaded Set
            </Badge>
          )}
          <Text size="xs" c="dimmed">
            {manual ? 'Uploaded' : 'Generated'} {AppFactoryUtils.formatWhen(set.created)}
          </Text>
          {!manual && set.proofSheetUrl && (
            <Anchor href={set.proofSheetUrl} target="_blank" rel="noopener noreferrer" size="xs">
              every file
            </Anchor>
          )}
        </Group>

        {newerLive && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" data-testid="draft-predates-live">
            This draft was made before the images that are live now (approved {AppFactoryUtils.formatWhen(newerLive.updated)}).
            Approving it replaces them; discard it to keep them.
          </Alert>
        )}

        {!passed && (
          <Alert icon={<IconLock size={16} />} color="red" title={C.blockedTitle} data-testid="draft-blocked">
            <Stack gap={6}>
              {fixes.map((f) => (
                <Text size="sm" key={f.key}>
                  {f.text}
                  <Text span c="dimmed" size="xs">
                    {' '}
                    ({f.screens.join(', ')})
                  </Text>
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {advice.length > 0 && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="How it will look on a TV" data-testid="tv-quality">
            {advice.map((m) => (
              <Text size="sm" key={m.code} c={m.known ? undefined : 'dimmed'}>
                {m.text}
              </Text>
            ))}
          </Alert>
        )}

        {manual && set.proofSheetUrl && <Image src={set.proofSheetUrl} alt="Preview of the uploaded images" radius="sm" mah={360} fit="contain" />}

        {files &&
          (scope === 'roku' ? (
            <RokuScreenPreviews files={files} />
          ) : (
            <TvDevicePreviews files={files} show={{ firetv: tv.includes('firetv'), googletv: tv.includes('googletv') }} />
          ))}

        <BrandKitQualityTable quality={set.quality} tv={tv} />

        <Group gap="sm">
          <Tooltip label={C.blockedTooltip} disabled={passed} withArrow>
            <Button onClick={() => onApprove(set)} loading={busy} disabled={disabled || !passed}>
              {C.approveOne}
            </Button>
          </Tooltip>
          <Button variant="default" onClick={() => onDiscard(set)} loading={busy} disabled={disabled}>
            {C.discardOne}
          </Button>
          {!passed && isOperator && (
            <Button variant="subtle" color="red" onClick={() => setOverriding(true)} disabled={disabled || busy} data-testid="draft-override">
              {C.overrideAction}
            </Button>
          )}
        </Group>
      </Stack>

      <Modal opened={overriding} onClose={() => setOverriding(false)} title={C.overrideTitle} centered>
        <Stack gap="sm">
          <Text size="sm">{C.overrideBody}</Text>
          <Textarea
            label={C.overrideReasonLabel}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            maxLength={BRAND_KIT_REASON_MAX}
            description={`${reason.length}/${BRAND_KIT_REASON_MAX}`}
            autosize
            minRows={2}
            required
            data-autofocus
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setOverriding(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={submitOverride} disabled={reason.trim() === ''}>
              {C.overrideConfirm}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
